import { useMemo } from 'react'

import { ChatGalleryRestControllerService } from '@api'
import { useUnit } from 'effector-react'
import { last } from 'lodash'
import { useInfiniteQuery } from 'react-query'

import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import { $creatorId, $creatorOFId } from '@/models/creators.ts'
import { updateMediaDataMap } from '@/models/vault/medias'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'

import { messageToMediaMessage } from '../state/sent-media-message-provider'
import {
  ChatMediaType,
  SentMediaMessage,
  SentMediaMessageMedia,
} from '../types'

export interface Props {
  selectedMediaType: ChatMediaType
}

export const useActiveChatGallery = ({ selectedMediaType }: Props) => {
  const fanId = useUnit($fanId)
  const creatorId = useUnit($creatorId)
  const creatorOFId = useUnit($creatorOFId)

  const {
    data,
    isSuccess,
    dataUpdatedAt,
    isLoading,
    remove,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['chat', 'gallery', creatorId, fanId, selectedMediaType],
    queryFn: ({ pageParam = '' }) =>
      ChatGalleryRestControllerService.getGallery({
        creatorId: creatorId!,
        fanId,
        type: selectedMediaType,
        lastId: pageParam as unknown as number,
        mapCdnUrls: true,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextLastId : undefined,
    onSuccess: (data) => {
      // TODO: we don't need it if BE will send media ids
      updateMediaDataMap(
        data.pages.flatMap(({ list }) =>
          list.flatMap(({ media, id, fromUser, releaseForms }) => {
            const isMessageFromFan = fromUser.id !== creatorOFId

            return (media ?? []).map(
              // @ts-ignore
              (media): StreamableVaultMedia => ({
                ...media,
                isExpirable: isMessageFromFan,
                relatedMessageId: isMessageFromFan ? id : undefined,
                relatedUserId: isMessageFromFan ? fromUser.id : undefined,
                // @ts-ignore
                releaseForms,
                createdAt: '',
                listStates: [],
              }),
            )
          }),
        ),
      )
    },
    enabled: !!creatorId && !!fanId,
    retry: false,
  })

  const allGallery = useMemo(
    () => data?.pages.flatMap(({ list }) => list) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.pages, dataUpdatedAt],
  )

  const mediaToMessageMap = useMemo(() => {
    const map = new Map<SentMediaMessageMedia, SentMediaMessage>()

    allGallery.forEach((gallery) => {
      const mediaMessage = messageToMediaMessage(fanId, gallery!)

      mediaMessage.media.forEach((media) => {
        if (
          ![
            ChatMediaType.Video,
            ChatMediaType.Photo,
            ChatMediaType.Audio,
          ].includes(selectedMediaType) ||
          media.type === selectedMediaType
        ) {
          map.set(media, mediaMessage)
        }
      })
    })

    return map
  }, [allGallery, fanId, selectedMediaType])

  const allMedias = useMemo(
    () => Array.from(mediaToMessageMap.entries()),
    [mediaToMessageMap],
  )

  const hasMore = last(data?.pages)?.hasMore ?? false

  const isEmpty = isSuccess && allGallery.length === 0

  return {
    allMedias,
    isLoading,
    isEmpty,
    isFetchingNextPage,
    hasMore,
    reset: remove,
    fetchNextPage,
  }
}
